import { useTRPC } from '@/utils/trpc';
import { formatHumanNameLastFirst, partitionProcedureReports } from '@liza/fhir-utils';
import { QueryStateHandler } from '@liza/ui/components/QueryStateHandler';
import { ProcedureDetail } from '@liza/ui/components/records';
import { getObservationIdentifier } from '@liza/ui/utils/observation';
import { resolveId } from '@medplum/core';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDiagnosticReportObservations } from './useDiagnosticReportObservations';

type Props = {
  id: string;
  /**
   * Clicking a row in an inlined report's results table navigates to that analyte's own detail —
   * its trend chart and result history, which live in the Labs view keyed by analyte identifier.
   * Named for the row, not the destination: a procedure is not a lab, and for an imaging report
   * the rows are narrative text whose analyte detail has little to show. Omit to make rows inert.
   */
  onSelectResultObservation?: (analyteIdentifier: string) => void;
  /** Navigate to the linked encounter in the Data tab. */
  onSelectEncounter?: (encounterId: string) => void;
  /** Open the linked encounter in the shared drawer. */
  onOpenEncounterInDrawer?: (encounterId: string) => void;
};

const formatPractitionerName = (firstName: string, lastName: string): string =>
  formatHumanNameLastFirst({ family: lastName || undefined, given: firstName ? [firstName] : undefined });

export const Procedure = ({
  id,
  onSelectResultObservation,
  onSelectEncounter,
  onOpenEncounterInDrawer,
}: Props): React.ReactElement => {
  const { t } = useTranslation('records');
  const trpc = useTRPC();

  const procedureQuery = useQuery(trpc.patient.procedure.get.queryOptions({ id }));
  const procedure = procedureQuery.data?.procedure;

  const encounterId = resolveId(procedure?.encounter);
  const encounterQuery = useQuery({
    ...trpc.patient.encounter.get.queryOptions({ id: encounterId ?? '' }),
    enabled: !!encounterId,
  });

  // `report[]` is `Reference<DiagnosticReport | DocumentReference>`, and both kinds occur in
  // practice — imported procedures overwhelmingly link DocumentReferences (scanned op notes,
  // anaesthesia evaluations), while a results-bearing report is a DiagnosticReport. They render
  // differently and are read by different endpoints, so the list is split by target type first.
  // Fetching every entry as a DiagnosticReport 404s the documents and would report the whole
  // section as failed.
  const { diagnosticReportIds, documentReferenceIds } = useMemo(
    () => partitionProcedureReports(procedure?.report),
    [procedure?.report],
  );

  const reportQueries = useQueries({
    queries: diagnosticReportIds.map((reportId) => trpc.patient.diagnosticReport.get.queryOptions({ id: reportId })),
  });

  const documentQueries = useQueries({
    queries: documentReferenceIds.map((docId) => trpc.patient.documentReference.get.queryOptions({ id: docId })),
  });

  const diagnosticReports = useMemo(
    () => reportQueries.map((query) => query.data?.diagnosticReport).filter((report) => !!report),
    [reportQueries],
  );

  const reportDocuments = useMemo(
    () => documentQueries.map((query) => query.data?.documentReference).filter((document) => !!document),
    [documentQueries],
  );

  const {
    loadedObservations,
    isLoading: isLoadingObservations,
    hasError: hasObservationsError,
  } = useDiagnosticReportObservations(diagnosticReports);

  const isLoadingReports = reportQueries.some((query) => query.isLoading);
  const hasReportError = reportQueries.some((query) => query.isError) || hasObservationsError;
  const isLoadingReportDocuments = documentQueries.some((query) => query.isLoading);
  const hasReportDocumentError = documentQueries.some((query) => query.isError);

  const practitionerRefs = useMemo(() => {
    const refs = procedure?.performer
      ?.map((performer) => performer.actor?.reference)
      .filter((ref): ref is string => !!ref?.startsWith('Practitioner/'));
    return refs ?? [];
  }, [procedure?.performer]);

  const practitionerQueries = useQueries({
    queries: practitionerRefs.map((ref) => {
      const practitionerId = resolveId({ reference: ref }) ?? '';
      return {
        ...trpc.practitioner.get.queryOptions({ practitionerId }),
        enabled: !!practitionerId,
      };
    }),
  });

  const performerDisplays = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    practitionerRefs.forEach((ref, i) => {
      const data = practitionerQueries[i]?.data;
      if (data) map[ref] = formatPractitionerName(data.firstName, data.lastName);
    });
    return map;
  }, [practitionerRefs, practitionerQueries]);

  return (
    <QueryStateHandler query={procedureQuery} emptyMessage={t('noProceduresFound')}>
      {(data) => (
        <ProcedureDetail
          procedure={data.procedure}
          // The linked visit and its results are progressive enhancement — a failed encounter
          // fetch leaves the block hidden rather than failing the whole detail view.
          encounter={encounterQuery.data?.encounter}
          isLoadingEncounter={!!encounterId && encounterQuery.isLoading}
          onSelectEncounter={onSelectEncounter}
          onOpenEncounterInDrawer={onOpenEncounterInDrawer}
          diagnosticReports={diagnosticReports}
          diagnosticReportObservations={loadedObservations}
          isLoadingDiagnosticReports={isLoadingReports || (diagnosticReports.length > 0 && isLoadingObservations)}
          hasDiagnosticReportError={hasReportError}
          reportDocuments={reportDocuments}
          isLoadingReportDocuments={isLoadingReportDocuments}
          hasReportDocumentError={hasReportDocumentError}
          onSelectObservation={
            onSelectResultObservation
              ? (observation): void => onSelectResultObservation(getObservationIdentifier(observation))
              : undefined
          }
          performerDisplays={performerDisplays}
        />
      )}
    </QueryStateHandler>
  );
};
